<template>
  <f7-page :page-content="true" name="reglascoincidencia" @page:beforein="cargar">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('reglascoincidencia.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-button icon-f7="wand_stars" :text="$t('reglascoincidencia.pedirIa')" class="display-flex" @click="pedirPropuestas"></f7-button>
        <f7-button icon-f7="plus" :text="$t('reglascoincidencia.nueva')" class="display-flex" @click="nueva"></f7-button>
        <f7-button icon-f7="arrow_clockwise" :text="$t('reglascoincidencia.recargar')" class="display-flex" @click="cargar"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('reglascoincidencia.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <f7-block strong class="cabecera-ia no-margin-bottom">
        <p class="pie">{{ $t('reglascoincidencia.pieIa') }}</p>
      </f7-block>

      <!-- Resultado de la última tanda: lo que se guardó y, sobre todo, lo que NO. Enseñar
           lo descartado con su motivo evita que el botón parezca que no ha hecho nada. -->
      <f7-card v-if="ultimaTanda" class="padding-vertical-half padding-right bloque">
        <f7-card-header>
          {{ $t('reglascoincidencia.resultadoIa') }}
          <f7-link icon-f7="xmark" @click="ultimaTanda = null"></f7-link>
        </f7-card-header>
        <f7-card-content>
          <!-- [revisión #1711] Los avisos de la tanda, que son lo único que dice que algo no
               salió entero. No se pintaban, y la API los manda sin `mensaje`:
               `FUNCIONES_PENDIENTES_51` llega así en todas las tandas hasta que entre la #51, y
               la tarjeta se quedaba en «0 propuestas nuevas · 0 no reconocidas» como si todo
               hubiera ido bien. El `mensaje` queda debajo porque es el detalle —el error del
               servicio, los títulos afectados—, no el titular. -->
          <p v-if="avisosTanda.length" class="avisos-tanda">
            <span v-for="a in avisosTanda" :key="a.codigo" class="etiqueta-aviso">{{ a.texto }}</span>
          </p>
          <p v-if="ultimaTanda.mensaje" class="pie aviso">{{ ultimaTanda.mensaje }}</p>
          <p>
            {{ $t('reglascoincidencia.resultadoIaCuenta', {
              nuevas: (ultimaTanda.propuestas || []).length,
              fuera: (ultimaTanda.descartadas || []).length
            }) }}
            <span class="origen">{{ $t('reglascoincidencia.modeloIa', { modelo: ultimaTanda.modelo || '—' }) }}</span>
          </p>
          <ul v-if="ultimaTanda.descartadas && ultimaTanda.descartadas.length" class="descartadas">
            <li v-for="(d, i) in ultimaTanda.descartadas" :key="i">
              <b>{{ d.titulo }}</b>
              <span class="etiqueta-aviso">{{ textoCodigo('reglascoincidencia.descartes', d.motivo) }}</span>
              <span class="detalle">{{ d.detalle }}</span>
            </li>
          </ul>
        </f7-card-content>
      </f7-card>

      <!-- Los contadores son también el filtro: pinchar en uno deja solo ese estado. -->
      <f7-block strong class="contadores no-margin-bottom">
        <span
          v-for="c in contadores"
          :key="c.clave"
          class="contador"
          :class="[c.clase, { 'contador-apagado': filtroEstado && filtroEstado != c.clave }]"
          @click="filtrarPor(c.clave)"
        >{{ c.texto }}: {{ c.valor }}</span>
        <!-- «Total» también se apaga con un filtro puesto: es el único que decía cuántas reglas
             hay en el catálogo, y encendido al lado de una tabla filtrada se leía como que no
             había ningún filtro (JJCS en preview-1, 01/10/2026). -->
        <span
          class="contador contador-total"
          :class="{ 'contador-apagado': !!filtroEstado }"
          @click="filtrarPor(null)"
        >{{ $t('reglascoincidencia.total') }}: {{ reglas.length }}</span>
      </f7-block>

      <!-- Alta y edición. La misma tarjeta sirve para las dos: lo único que cambia es si
           hay id o no, y el botón lo dice. -->
      <f7-card v-if="editando" class="padding-vertical-half padding-right bloque">
        <f7-card-header>
          {{ tituloFormulario }}
        </f7-card-header>
        <f7-card-content>
          <!-- La clave cambia en cada alta o edición para forzar el remontaje: `r-form` copia
               `initialData` solo al crearse, así que sin esto pulsar «Editar» en una segunda
               regla dejaría el formulario con los datos de la primera y guardarlos encima. -->
          <r-form ref="formRegla" :key="claveFormulario" v-slot="{ rform }" :initialData="editando">
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="100" medium="50">
                <ul>
                  <r-input outline floating-label :label="$t('reglascoincidencia.tituloRegla')" :form="rform" type="text" name="titulo"></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="25">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('reglascoincidencia.tipoMiembros')"
                    :form="rform"
                    type="select"
                    name="tipoMiembros"
                  >
                    <option v-for="t in tipos" :key="t" :value="t">{{ $t('reglascoincidencia.tipos.' + t) }}</option>
                  </r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="25">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('reglascoincidencia.severidad')"
                    :form="rform"
                    type="select"
                    name="severidad"
                  >
                    <option v-for="s in severidades" :key="s" :value="s">{{ $t('coincidencias.severidades.' + s) }}</option>
                  </r-input>
                </ul>
              </f7-col>

              <f7-col v-if="rform.formData.tipoMiembros == 'PERSONAS'" width="100" medium="50">
                <ul>
                  <r-autocomplete-m2m
                    outline
                    floating-label
                    :dbAdapter="Empleado"
                    :label="$t('reglascoincidencia.personas')"
                    :form="rform"
                    name="personas"
                    idProp="username"
                    labelProp="nombre"
                    prefixProp="username"
                    type="textarea"
                    :lineBreak="true"
                    :showNavigationLink="false"
                    :filter="{ activo: true }"
                  ></r-autocomplete-m2m>
                </ul>
              </f7-col>
              <f7-col v-if="rform.formData.tipoMiembros == 'MATERIA_ENAC'" width="100" medium="50">
                <ul>
                  <r-autocomplete
                    outline
                    floating-label
                    :dbAdapter="Materia"
                    :label="$t('reglascoincidencia.materia')"
                    :form="rform"
                    name="materia"
                    idProp="codigo"
                    labelProp="nombre"
                    prefixProp="codigo"
                  ></r-autocomplete>
                </ul>
              </f7-col>
              <f7-col v-if="rform.formData.tipoMiembros == 'FUNCIONES'" width="100" medium="50">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('reglascoincidencia.funciones')"
                    :form="rform"
                    type="text"
                    name="funcionesTexto"
                  ></r-input>
                </ul>
              </f7-col>

              <f7-col width="100" medium="25">
                <ul>
                  <r-input outline floating-label :label="$t('reglascoincidencia.minimoPresentes')" :form="rform" type="number" name="minimoPresentes"></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="75">
                <ul>
                  <r-input outline floating-label :label="$t('reglascoincidencia.explicacion')" :form="rform" type="text" name="explicacion"></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="75">
                <ul>
                  <r-input outline floating-label :label="$t('reglascoincidencia.motivo')" :form="rform" type="text" name="motivo"></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="25" class="display-flex justify-content-center align-items-center">
                <f7-button fill icon-f7="floppy_disk" :text="$t('reglascoincidencia.guardar')" class="display-flex" @click="guardar"></f7-button>
                <f7-button :text="$t('reglascoincidencia.cancelar')" class="display-flex margin-left-half" @click="editando = null"></f7-button>
              </f7-col>
            </f7-row>
            <!-- Dentro del slot para que el aviso siga al selector: `rform.formData` es lo
                 reactivo; leerlo por `$refs` desde una computed no se recalcularía nunca. -->
            <!-- Ya no es un aviso naranja: desde la #51 las reglas por funciones sí incluyen a
                 gente, así que el texto explica de dónde salen sus miembros en vez de decir que
                 todavía no hay ninguno (revisión CODEX del PR #1732). -->
            <p v-if="rform.formData.tipoMiembros == 'FUNCIONES'" class="pie">
              {{ $t('reglascoincidencia.avisoFunciones') }}
            </p>
          </r-form>
          <p v-if="editando.sustituyeA" class="pie aviso">{{ $t('reglascoincidencia.pieSustituir') }}</p>
          <p class="pie">{{ $t('reglascoincidencia.pieAlta') }}</p>
        </f7-card-content>
      </f7-card>

      <f7-block strong class="tabla-wrapper">
        <table class="tabla-reglas">
          <thead>
            <tr>
              <th>{{ $t('reglascoincidencia.tituloRegla') }}</th>
              <th>{{ $t('reglascoincidencia.miembros') }}</th>
              <th class="col-centro">{{ $t('reglascoincidencia.minimoPresentes') }}</th>
              <th>{{ $t('reglascoincidencia.severidad') }}</th>
              <th>{{ $t('reglascoincidencia.estado') }}</th>
              <th>{{ $t('reglascoincidencia.acciones') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="regla in reglasFiltradas">
              <tr :key="regla.id" :class="{ descartada: regla.estado == 'DESCARTADA' }">
                <td>
                  <a href="#" @click.prevent="alternarHistorico(regla)">{{ regla.titulo }}</a>
                  <span class="tipo">{{ $t('reglascoincidencia.tipos.' + regla.tipoMiembros) }}</span>
                  <span v-if="regla.explicacion" class="explicacion">{{ regla.explicacion }}</span>
                </td>
                <td class="col-miembros">
                  <!-- Nombre completo a la vista y usuario al pasar el ratón
                       (JJCS, 21/09/2026): el nombre es lo que identifica a la persona, y el
                       usuario hace falta para cotejar con la ERP, no para leer la regla. -->
                  <span
                    v-for="miembro in (regla.miembros || [])"
                    :key="miembro.username"
                    class="chip-miembro"
                    :title="miembro.username"
                  >{{ miembro.nombre || miembro.username }}</span>
                  <span v-if="!(regla.miembros || []).length">—</span>
                  <span
                    v-for="aviso in regla.avisos"
                    :key="aviso"
                    class="etiqueta-aviso"
                  >{{ textoCodigo('reglascoincidencia.avisos', aviso) }}</span>
                </td>
                <td class="col-centro">{{ regla.minimoPresentes }}</td>
                <td>
                  <span class="etiqueta-severidad" :class="'sev-' + regla.severidad">
                    {{ $t('coincidencias.severidades.' + regla.severidad) }}
                  </span>
                </td>
                <td>
                  <span class="etiqueta-estado" :class="'estado-' + regla.estado">{{ $t('reglascoincidencia.estados.' + regla.estado) }}</span>
                  <span class="origen">{{ $t('reglascoincidencia.origenes.' + regla.origen) }}</span>
                  <span v-if="regla.sustituidaPor" class="origen">
                    {{ $t('reglascoincidencia.sustituidaPor', { titulo: regla.sustituidaPorTitulo || regla.sustituidaPor }) }}
                  </span>
                </td>
                <td class="col-acciones">
                  <!-- Quién se puede editar lo decide la API y viaja en «editable»: una propuesta
                       sustituida no (lo que haya que cambiar se edita en la regla que la sustituyó)
                       y una propuesta descartada sin llegar a estar en vigor, tampoco (se guarda
                       tal y como la propuso la IA; para usarla hay que reactivarla). Se calcula
                       allí porque depende del histórico de la regla, no de lo que se ve aquí. -->
                  <a v-if="regla.editable" href="#" @click.prevent="editar(regla)">{{ $t('reglascoincidencia.editar') }}</a>
                  <a v-if="regla.estado == 'PROPUESTA'" href="#" @click.prevent="cambiarEstado(regla, 'ACEPTADA')">{{ $t('reglascoincidencia.aceptar') }}</a>
                  <a v-if="regla.estado != 'DESCARTADA'" href="#" @click.prevent="cambiarEstado(regla, 'DESCARTADA')">{{ $t('reglascoincidencia.descartar') }}</a>
                  <!-- Una propuesta sustituida no se reactiva: la que está en vigor es la que la
                       sustituyó, y las dos a la vez evaluarían dos veces la misma coincidencia.
                       La API también lo rechaza; aquí se evita ofrecer el enlace. -->
                  <a v-if="regla.estado == 'DESCARTADA' && !regla.sustituidaPor" href="#" @click.prevent="cambiarEstado(regla, 'ACEPTADA')">{{ $t('reglascoincidencia.reactivar') }}</a>
                </td>
              </tr>
              <tr v-if="historicoAbierto == regla.id" :key="regla.id + '-hist'" class="fila-historico">
                <td colspan="6">
                  <div class="historico-titulo">{{ $t('reglascoincidencia.historico') }}</div>
                  <ul class="historico">
                    <li v-for="(linea, i) in regla.historico" :key="i">
                      <span class="fecha">{{ linea.fecha | dateFormat }}</span>
                      <b>{{ $t('reglascoincidencia.acciones_audit.' + linea.accion) }}</b>
                      <span class="quien">{{ linea.usuario }}</span>
                      <span v-if="linea.motivo" class="motivo">«{{ linea.motivo }}»</span>
                      <span v-if="linea.antes" class="cambio">{{ linea.antes }} → {{ linea.despues }}</span>
                    </li>
                    <li v-if="!regla.historico || !regla.historico.length">{{ $t('reglascoincidencia.sinHistorico') }}</li>
                  </ul>
                </td>
              </tr>
            </template>
            <tr v-if="!reglasFiltradas.length">
              <td colspan="6" class="vacio">
                {{ textoTablaVacia }}
                <a
                  v-if="filtroEstado && reglas.length"
                  href="#"
                  @click.prevent="filtrarPor(null)"
                >{{ $t('reglascoincidencia.verTodas') }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </f7-block>

      <!-- Comprobar fechas: el mismo motor que verá el solicitante, aquí para que quien
           escribe las reglas vea en el momento qué pintan. -->
      <f7-card class="padding-vertical-half padding-right bloque">
        <f7-card-header>{{ $t('reglascoincidencia.comprobar') }}</f7-card-header>
        <f7-card-content>
          <!-- Tocar la persona o las fechas borra el semáforo anterior: dejarlo debajo de unos
               datos distintos hace creer que es el de lo que se está viendo. El `change` del
               formulario es el que trae el autocompletar (también al vaciarlo); los calendarios
               emiten el suyo. -->
          <r-form ref="formPrueba" v-slot="{ rform }" :initialData="prueba" @change="invalidarComprobacion">
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="100" medium="40">
                <ul>
                  <r-autocomplete
                    outline
                    floating-label
                    :dbAdapter="Empleado"
                    :label="$t('reglascoincidencia.persona')"
                    :form="rform"
                    name="username"
                    idProp="username"
                    labelProp="nombre"
                    prefixProp="username"
                    :filter="{ filter: { activo: true } }"
                  ></r-autocomplete>
                </ul>
              </f7-col>
              <f7-col width="100" medium="20">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('reglascoincidencia.desde')"
                    :form="rform"
                    type="datepicker"
                    name="fechaInicio"
                    :calendar-params="{ closeOnSelect: true, routableModals: false, dateFormat: { year: 'numeric', month: '2-digit', day: '2-digit' } }"
                    @change="invalidarComprobacion"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="20">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('reglascoincidencia.hasta')"
                    :form="rform"
                    type="datepicker"
                    name="fechaFin"
                    :calendar-params="{ closeOnSelect: true, routableModals: false, dateFormat: { year: 'numeric', month: '2-digit', day: '2-digit' } }"
                    @change="invalidarComprobacion"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="20" class="display-flex justify-content-center align-items-center">
                <f7-button fill icon-f7="checkmark_seal" :text="$t('reglascoincidencia.comprobarBoton')" class="display-flex" @click="comprobar"></f7-button>
              </f7-col>
            </f7-row>
          </r-form>
          <r-semaforo-coincidencias
            :evaluacion="evaluacion"
            @elegir-alternativa="probarAlternativa"
          ></r-semaforo-coincidencias>
          <!-- Solo si la respuesta viene con nombres: la explicación de la IA es la del
               aprobador. Quien está aquí es ADMIN o DIRECTOR, pero si comprueba sus propias
               fechas el API le contesta en anónimo, como a cualquier solicitante, y entonces
               esto no le corresponde. El mismo componente es el que irá en la bandeja del
               aprobador (#59). -->
          <r-explicacion-ia
            v-if="evaluacion && evaluacion.conNombres"
            :solicitud="solicitudComprobada"
            @motivo="motivoRedactado"
          ></r-explicacion-ia>
          <p class="pie">{{ $t('reglascoincidencia.pieComprobar') }}</p>
        </f7-card-content>
      </f7-card>
    </template>
  </f7-page>
</template>
<style scoped>
.contadores .contador {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  margin-right: 8px;
  margin-bottom: 4px;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
}
.contador-apagado { opacity: 0.35; }
.contador-verde { background-color: #4cd964; }
.contador-ambar { background-color: #ff9500; }
.contador-gris { background-color: #8e8e93; }
.contador-total { background-color: #444; }
.bloque .card-header { font-weight: bold; color: #22355b; }
.bloque .pie { font-size: 12px; color: #8e8e93; margin: 0 16px 8px; }
.bloque .pie.aviso { color: #b26a00; }
.tabla-wrapper { overflow-x: auto; }
.tabla-reglas { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-reglas th,
.tabla-reglas td {
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-reglas th { background-color: #f4f5f7; font-weight: bold; }
.tabla-reglas .col-centro { text-align: center; }
.tabla-reglas .col-miembros { max-width: 280px; word-break: break-word; }
.tabla-reglas .col-acciones a { margin-right: 8px; white-space: nowrap; }
.tabla-reglas tr.descartada td { color: #9aa1a8; font-style: italic; }
.tabla-reglas .tipo,
.tabla-reglas .origen { display: block; font-size: 11px; color: #8e8e93; }
.tabla-reglas .explicacion { display: block; font-size: 11px; color: #555; font-style: italic; }
.tabla-reglas .vacio { text-align: center; color: #8e8e93; padding: 16px; }
.etiqueta-estado,
.etiqueta-severidad,
.etiqueta-aviso {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
  margin-right: 4px;
}
.etiqueta-aviso { background-color: #ff9500; }
.sev-BLOQUEANTE { background-color: #22355b; }
.sev-AVISO { background-color: #ff9500; }
.estado-ACEPTADA { background-color: #4cd964; }
.estado-PROPUESTA { background-color: #ff9500; }
.estado-DESCARTADA { background-color: #8e8e93; }
.cabecera-ia .pie { font-size: 12px; color: #8e8e93; margin: 0; }
.chip-miembro {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background-color: #eef1f5;
  color: #22355b;
  font-size: 12px;
  margin: 0 4px 2px 0;
  cursor: default;
}
/* Las etiquetas de la tanda envuelven: algunas son frases cortas, no una palabra. */
.avisos-tanda { margin: 0 0 6px; line-height: 1.9; }
.descartadas { list-style: none; margin: 4px 0 0; padding: 0; font-size: 13px; }
.descartadas li { padding: 2px 0; }
.descartadas .detalle { display: block; color: #8e8e93; font-size: 12px; }
.fila-historico td { background-color: #fafbfc; }
.historico-titulo { font-weight: bold; margin-bottom: 2px; }
.historico { list-style: none; margin: 0; padding: 0; font-size: 12px; }
.historico .fecha { color: #8e8e93; margin-right: 6px; }
.historico .quien { margin-left: 6px; color: #22355b; }
.historico .motivo { margin-left: 6px; font-style: italic; }
.historico .cambio { display: block; color: #8e8e93; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import rAutocompleteM2m from "./../../components/rAutocompleteM2m.vue";
import rSemaforoCoincidencias from "./../../components/rSemaforoCoincidencias.vue";
import rExplicacionIa from "./../../components/rExplicacionIa.vue";
import Empleado from "./../../js/db/Empleado.js";
import Materia from "./../../js/db/Materia.js";
import RhCoincidencias from "./../../js/db/RhCoincidencias.js";
import textoCodigo from "./../../js/utils/textoCodigo.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

// Avisos que la API devuelve cuando el cambio quedó escrito pero algo posterior falló. No son
// del catálogo: viajan en la respuesta de guardar o de cambiar de estado, y por eso se sacan al
// terminar en vez de esperarlos en la fila de la regla.
// `FUNCIONES_SIN_TRADUCIR` entra aquí por lo mismo que los otros tres: viene de la respuesta a
// guardar o a cambiar de estado, el cambio ya está hecho, y lo que dice —que las funciones se
// están enseñando por su código interno y no por el que se ve— no se distingue a ojo en la tabla
// (#483).
const INCIDENCIAS_ESCRITURA = ['ALTA_SIN_RASTRO', 'CAMBIO_SIN_RASTRO', 'REGLA_SIN_FICHA',
  'FUNCIONES_SIN_TRADUCIR'];

/**
 * Ajustes · Reglas de coincidencia (#64). El catálogo de «quién no puede coincidir de
 * vacaciones» y, debajo, un comprobador de fechas que llama al mismo motor que verá el
 * solicitante: una regla creada a mano nace aceptada y funciona al momento, y aquí se ve.
 *
 * El catálogo entero llega en una petición y se filtra en memoria: son unas pocas decenas de
 * reglas, como mucho.
 */
export default {
  components: {
    rForm,
    rInput,
    rAutocomplete,
    rAutocompleteM2m,
    rSemaforoCoincidencias,
    rExplicacionIa
  },
  data: function() {
    return {
      Empleado,
      Materia,
      reglas: [],
      filtroEstado: null,
      editando: null,
      // Contador, no el id de la regla: así también se vacía el formulario al editar dos veces
      // seguidas la misma o al encadenar dos altas nuevas.
      claveFormulario: 0,
      historicoAbierto: null,
      prueba: { username: null, fechaInicio: null, fechaFin: null },
      evaluacion: null,
      solicitudComprobada: null,
      // Turno de la última comprobación pedida, como en `rExplicacionIa`: si se pulsa
      // «Comprobar» dos veces, la respuesta de la primera no puede pintar su semáforo encima
      // del de la segunda, que es la que se está mirando.
      turnoComprobacion: 0,
      // Mismo turno, pero para el catálogo: aceptar reglas de una recarga anterior a la última
      // mutación devolvería a la tabla los estados de antes de aceptar o descartar.
      turnoCarga: 0,
      ultimaTanda: null,
      error403: false,
      tipos: ['PERSONAS', 'MATERIA_ENAC', 'FUNCIONES'],
      severidades: ['BLOQUEANTE', 'AVISO']
    };
  },
  computed: {
    contadores: function() {
      var self = this;
      function cuantas(estado) {
        return self.reglas.filter(function(r) { return r.estado == estado; }).length;
      }
      return [
        { clave: 'ACEPTADA', clase: 'contador-verde', texto: self.$t('reglascoincidencia.estados.ACEPTADA'), valor: cuantas('ACEPTADA') },
        { clave: 'PROPUESTA', clase: 'contador-ambar', texto: self.$t('reglascoincidencia.estados.PROPUESTA'), valor: cuantas('PROPUESTA') },
        { clave: 'DESCARTADA', clase: 'contador-gris', texto: self.$t('reglascoincidencia.estados.DESCARTADA'), valor: cuantas('DESCARTADA') }
      ];
    },
    reglasFiltradas: function() {
      var self = this;
      if (!self.filtroEstado) return self.reglas;
      return self.reglas.filter(function(r) { return r.estado == self.filtroEstado; });
    },
    /**
     * Lo que se lee cuando la tabla sale vacía. «No hay ninguna regla» y «no hay ninguna de
     * ESTE estado» son dos cosas muy distintas, y con los contadores al lado enseñando el
     * catálogo entero la segunda se leía como que la pantalla había perdido las reglas. Se dice
     * cuántas hay y por qué no se ven, y el enlace de al lado quita el filtro.
     *
     * Está aquí y no en cada sitio que toca `filtroEstado` a propósito: a una tabla filtrada y
     * vacía se llega por varios caminos —filtrando a mano, pidiendo propuestas, o aceptando la
     * última que quedaba por revisar—, y el texto tiene que explicarse solo en todos.
     */
    textoTablaVacia: function() {
      if (!this.filtroEstado || !this.reglas.length) {
        return this.$t('reglascoincidencia.sinResultados');
      }
      return this.$t('reglascoincidencia.sinResultadosFiltro', {
        total: this.reglas.length,
        estado: this.$t('reglascoincidencia.estados.' + this.filtroEstado)
      });
    },
    tituloFormulario: function() {
      if (!this.editando) return '';
      // Editar una propuesta no la edita: crea una regla nueva con lo editado. El título lo
      // dice antes de guardar, no después.
      if (this.editando.sustituyeA) return this.$t('reglascoincidencia.editarPropuesta');
      return this.editando.id ? this.$t('reglascoincidencia.editar') : this.$t('reglascoincidencia.nueva');
    },
    /**
     * Los avisos de la última tanda, sin repetidos: la API añade `PROPUESTA_SIN_FICHA` una vez
     * por propuesta que falla, y la misma etiqueta dos veces no cuenta nada nuevo.
     */
    avisosTanda: function() {
      const self = this;
      const codigos = [];
      ((self.ultimaTanda || {}).avisos || []).forEach(function(aviso) {
        if (codigos.indexOf(aviso) === -1) codigos.push(aviso);
      });
      return codigos.map(function(codigo) {
        return { codigo: codigo, texto: self.textoCodigo('reglascoincidencia.avisosTanda', codigo) };
      });
    }
  },
  methods: {
    textoCodigo: function(espacio, codigo) {
      return textoCodigo(this, espacio, codigo);
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    /**
     * Cambiar el catálogo deja el semáforo de «Comprobar fechas» calculado con las reglas de
     * antes, y cambiar los datos de prueba lo deja calculado con la persona o las fechas de
     * antes. En los dos casos se borra: un semáforo viejo debajo de un formulario nuevo es peor
     * que no tener semáforo, porque no se distingue de uno recién calculado.
     */
    invalidarComprobacion: function() {
      this.evaluacion = null;
      this.solicitudComprobada = null;
      // Y lo que estuviera en vuelo deja de valer: si no, llegaría después y repintaría el
      // semáforo que se acaba de borrar por haber tocado una regla.
      this.turnoComprobacion++;
    },
    filtrarPor: function(estado) {
      this.filtroEstado = this.filtroEstado == estado ? null : estado;
    },
    /** ¿Queda alguna propuesta por revisar en la lista que hay cargada ahora mismo? */
    hayPropuestas: function() {
      return this.reglas.some(function(r) { return r.estado == 'PROPUESTA'; });
    },
    alternarHistorico: function(regla) {
      this.historicoAbierto = this.historicoAbierto == regla.id ? null : regla.id;
    },
    nueva: function() {
      this.claveFormulario++;
      this.editando = {
        id: null,
        sustituyeA: null,
        titulo: null,
        tipoMiembros: 'PERSONAS',
        personas: [],
        funcionesTexto: null,
        identidadesFunciones: {},
        materia: null,
        minimoPresentes: 1,
        severidad: 'BLOQUEANTE',
        explicacion: null,
        motivo: null
      };
    },
    editar: function(regla) {
      // «Editar» una propuesta de la IA abre este mismo formulario relleno, pero al guardar no
      // modifica la propuesta: crea una regla manual aceptada y deja la propuesta descartada y
      // marcada como sustituida (JJCS, 21/09/2026). Por eso va sin `id` y con `sustituyeA`.
      const esPropuesta = regla.estado == 'PROPUESTA';
      this.claveFormulario++;
      this.editando = {
        id: esPropuesta ? null : regla.id,
        sustituyeA: esPropuesta ? regla.id : null,
        titulo: regla.titulo,
        tipoMiembros: regla.tipoMiembros,
        personas: (regla.personas || []).slice(),
        funcionesTexto: (regla.funciones || []).join(', '),
        identidadesFunciones: this.identidadesDeFunciones(regla),
        materia: regla.materia,
        minimoPresentes: regla.minimoPresentes,
        severidad: regla.severidad,
        explicacion: regla.explicacion,
        motivo: null
      };
    },
    /**
     * La identidad de cada función de la regla, indexada por el código que se ve:
     * `F-DIR-01` → `FN-0007`.
     *
     * El campo se escribe con los códigos que se ven, y ésos se reordenan: el reordenado del
     * catálogo es justo para lo que está la #483. Si alguien renumera mientras este formulario
     * está abierto, guardar la regla —aunque sólo se le haya cambiado el título— la dejaba
     * apuntando a la función que lleva ESE código ahora, porque lo que se manda es el código
     * visible y el backend lo traduce con el catálogo de ese momento (revisión del PR #2059,
     * r4241649603). Con esta tabla, lo que no se ha tocado se manda ya por su código interno,
     * que no cambia nunca.
     */
    identidadesDeFunciones: function(regla) {
      const porVisible = {};
      const internas = regla.funcionesInternas || [];
      (regla.funciones || []).forEach(function(visible, i) {
        if (visible && internas[i]) {
          porVisible[String(visible).trim().toUpperCase()] = internas[i];
        }
      });
      return porVisible;
    },
    /**
     * Los códigos que se mandan al guardar: el interno para lo que venía de la regla y sigue
     * escrito igual, y lo tecleado tal cual para lo demás —una función que se añade ahora no
     * tiene identidad que conservar, y el backend la traduce—. Ver `identidadesDeFunciones`.
     */
    funcionesAGuardar: function(texto) {
      const identidades = (this.editando && this.editando.identidadesFunciones) || {};
      return (texto || '').split(',')
        .map(function(f) { return f.trim(); })
        .filter(function(f) { return !!f; })
        .map(function(f) { return identidades[f.toUpperCase()] || f; });
    },
    guardar: function() {
      var self = this;
      var app = self.$f7;
      var datos = self.$refs.formRegla ? self.$refs.formRegla.formData : {};
      var cuerpo = {
        titulo: datos.titulo,
        tipoMiembros: datos.tipoMiembros,
        personas: datos.personas || [],
        // Se escriben separadas por comas y se mandan como lista; cuando entre la #51 esto
        // será un autocompletar contra su catálogo de funciones.
        // Aquí se escriben y se leen los códigos que se VEN en Ajustes · Funciones (F-DIR-01):
        // el backend los traduce al código interno, que es lo que guarda la regla desde la #483,
        // y los devuelve traducidos otra vez al pintarla. Así reordenar un código no deja la
        // regla apuntando a otra función.
        funciones: self.funcionesAGuardar(datos.funcionesTexto),
        materia: datos.materia,
        minimoPresentes: parseInt(datos.minimoPresentes, 10),
        severidad: datos.severidad,
        explicacion: datos.explicacion,
        motivo: datos.motivo
      };
      // #663 · Cada operación lleva su espera de la sesión, en vez del contador `bloqueos`: si la
      // sesión cambia en vuelo, la frontera calla el `then` que lo descontaba y el preloader se
      // quedaba puesto. Lo de la revisión #1711 lo sigue cumpliendo `esperaGlobal`: el modal no
      // se quita hasta que se suelta la última espera, así que una recarga vieja que termina
      // antes no deja la tabla operable mientras la buena sigue viajando.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      let peticion;
      if (self.editando.sustituyeA) {
        peticion = RhCoincidencias.sustituirPropuesta(self.editando.sustituyeA, cuerpo);
      } else if (self.editando.id) {
        peticion = RhCoincidencias.editar(self.editando.id, cuerpo);
      } else {
        peticion = RhCoincidencias.crear(cuerpo);
      }
      const aviso = self.editando.sustituyeA
        ? self.$t('reglascoincidencia.propuestaSustituida')
        : self.$t('reglascoincidencia.guardada');
      peticion
        .then(function(guardada) {
          app.toast.create({ text: aviso, position: 'center', closeTimeout: 2500 }).open();
          self.editando = null;
          self.invalidarComprobacion();
          // [revisión #1711] Se espera a la recarga: soltar el preloader antes dejaba la tabla
          // operable con la lista anterior, y encima el aviso de lo que acaba de pasar se leía
          // junto a unos estados que ya no eran los de la regla.
          return self.cargar().then(function() {
            self.avisarIncidencias(guardada);
          });
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('reglascoincidencia.errorGuardando'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    /**
     * Lo que la API no pudo terminar DESPUÉS de dejar el cambio escrito: el apunte de auditoría
     * o los miembros con los que se pinta la regla. Va en un aviso, no en un error, porque el
     * alta o el cambio de estado están hechos: presentarlo como fallo hacía que se repitiera la
     * operación y salían reglas duplicadas.
     *
     * Solo estos códigos: los demás avisos de una regla —sin miembros, mínimo inalcanzable— son
     * del catálogo y ya se ven en su fila; sacarlos aquí sería una ventana cada vez que se
     * guarda.
     */
    avisarIncidencias: function(regla) {
      const self = this;
      const incidencias = ((regla || {}).avisos || []).filter(function(aviso) {
        return INCIDENCIAS_ESCRITURA.indexOf(aviso) !== -1;
      });
      if (!incidencias.length) {
        return;
      }
      self.$f7.dialog.alert(
        incidencias.map(function(aviso) { return self.textoCodigo('reglascoincidencia.avisos', aviso); }).join('<br><br>'),
        self.$t('reglascoincidencia.incidenciaEscritura'));
    },
    cambiarEstado: function(regla, estado) {
      var self = this;
      var app = self.$f7;
      // Descartar y reactivar exigen motivo; aceptar una propuesta, no. Se pregunta solo
      // cuando hace falta para no pedir texto por pedirlo.
      var exigeMotivo = estado == 'DESCARTADA' || regla.estado == 'DESCARTADA';
      if (!exigeMotivo) {
        self.enviarEstado(regla, estado, null);
        return;
      }
      app.dialog.prompt(self.$t('reglascoincidencia.pideMotivo'), self.$t('reglascoincidencia.titulo'),
        function(motivo) {
          if (!motivo || !motivo.trim()) {
            app.dialog.alert(self.$t('reglascoincidencia.motivoObligatorio'));
            return;
          }
          self.enviarEstado(regla, estado, motivo.trim());
        });
    },
    enviarEstado: function(regla, estado, motivo) {
      var self = this;
      var app = self.$f7;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhCoincidencias.cambiarEstado(regla.id, estado, motivo)
        .then(function(actualizada) {
          self.invalidarComprobacion();
          // [revisión #1711] Igual que al guardar: hasta que la tabla no está recargada no se
          // suelta el preloader, o se puede volver a pulsar «aceptar» o «descartar» sobre la
          // fila con el estado viejo.
          return self.cargar().then(function() {
            self.avisarIncidencias(actualizada);
          });
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('reglascoincidencia.errorEstado'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    /**
     * «Pedir propuestas a la IA». Primero se pregunta cuántas quedan sin revisar y se avisa:
     * las pendientes no se sustituyen, se quedan, y sin el aviso la pantalla acaba llena.
     */
    pedirPropuestas: function() {
      const self = this;
      const app = self.$f7;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhCoincidencias.previoPropuestasIa()
        .then(function(previo) {
          espera.cerrar();
          if (previo && previo.iaHabilitada === false) {
            app.dialog.alert(self.$t('reglascoincidencia.iaApagada'));
            return;
          }
          if (previo && previo.aviso) {
            app.dialog.confirm(previo.aviso + ' ' + self.$t('reglascoincidencia.seguirPidiendo'),
              self.$t('reglascoincidencia.titulo'), function() {
                self.lanzarPropuestas();
              });
            return;
          }
          self.lanzarPropuestas();
        })
        .catch(function(e) {
          espera.cerrar();
          app.dialog.alert(e && e.message ? e.message : self.$t('reglascoincidencia.errorIa'));
        });
    },
    lanzarPropuestas: function() {
      const self = this;
      const app = self.$f7;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhCoincidencias.pedirPropuestasIa()
        .then(function(res) {
          self.ultimaTanda = res || null;
          self.invalidarComprobacion();
          // [revisión #1711] Se espera a la recarga antes de soltar el preloader: si no, el
          // resultado anuncia las propuestas nuevas mientras la tabla sigue con la lista
          // anterior, y en una conexión lenta da tiempo a pedir otra tanda o a aceptar y
          // descartar sobre estados viejos.
          return self.cargar().then(function() {
            // El filtro se pone DESPUÉS de la recarga y solo si hay algo que filtrar. Ponerlo
            // antes y a ciegas dejaba la pantalla filtrada por «Por revisar» aunque la tanda no
            // hubiera dejado ninguna: con el asistente caído se veía «Aceptada: 3 · Total: 3» y
            // la tabla diciendo «Ninguna regla con ese filtro» (JJCS en preview-1, 01/10/2026).
            // Era cierto, pero se leía como que la pantalla había perdido las reglas.
            if (self.hayPropuestas()) self.filtroEstado = 'PROPUESTA';
          });
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('reglascoincidencia.errorIa'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    /**
     * Una fecha alternativa del semáforo: se lleva al formulario y se vuelve a comprobar. Las
     * alternativas se pintan como enlaces, así que tienen que hacer algo; y quien está en
     * Ajustes lo que quiere es ver qué pintaría ese otro tramo.
     */
    probarAlternativa: function(alternativa) {
      if (!alternativa || !this.$refs.formPrueba) return;
      const datos = this.$refs.formPrueba.formData;
      this.$set(datos, 'fechaInicio', this.deIso(alternativa.fechaInicio));
      this.$set(datos, 'fechaFin', this.deIso(alternativa.fechaFin));
      this.comprobar();
    },
    /** Nada de lo que escribe la IA o un usuario entra tal cual en el HTML de un diálogo. */
    escaparHtml: function(texto) {
      return String(texto == null ? '' : texto)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    },
    /**
     * «Usar este motivo» del borrador de la IA. Aquí no hay ninguna solicitud que aprobar
     * —esto es el comprobador de Ajustes—, así que el motivo se copia al portapapeles y se
     * dice: quien aprueba de verdad lo hará desde la bandeja del aprobador (#59), que monta
     * este mismo componente y sí tiene dónde ponerlo.
     */
    motivoRedactado: function(evento) {
      const self = this;
      const texto = evento && evento.motivo ? evento.motivo : '';
      if (!texto) return;
      const copiado = function() {
        self.$f7.toast.create({
          text: self.$t('reglascoincidencia.motivoCopiado'),
          position: 'center',
          closeTimeout: 2500
        }).open();
      };
      // Si el navegador rechaza la copia —permisos, política o foco perdido— se enseña el texto
      // para copiarlo a mano: decir «motivo copiado» sin esperar a la promesa deja al aprobador
      // pegando un portapapeles vacío.
      //
      // Escapado antes de entrar: el contenido de `dialog.alert` se interpreta como HTML y este
      // texto lo ha redactado la IA a partir de datos que rellena cualquiera, así que una
      // etiqueta colada en el borrador se ejecutaría con la sesión de quien está en Ajustes
      // (ADMIN/DIRECTOR). Los saltos de línea se pasan a <br> porque el borrador viene en varias
      // líneas y sin eso se lee todo seguido.
      const aMano = function() {
        self.$f7.dialog.alert(self.escaparHtml(texto).replace(/\n/g, '<br>'),
          self.$t('reglascoincidencia.motivoRedactado'));
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(texto).then(copiado).catch(aMano);
        return;
      }
      aMano();
    },
    comprobar: function() {
      var self = this;
      var app = self.$f7;
      var datos = self.$refs.formPrueba ? self.$refs.formPrueba.formData : {};
      // El semáforo anterior se apaga antes de nada: si faltan datos o el API falla, dejarlo
      // puesto lo colgaría de la persona o las fechas nuevas, que no son las que evaluó.
      self.evaluacion = null;
      if (!datos.username || !datos.fechaInicio || !datos.fechaFin) {
        app.dialog.alert(self.$t('reglascoincidencia.faltanDatosPrueba'));
        return;
      }
      const solicitud = {
        username: datos.username,
        fechaInicio: self.aIso(datos.fechaInicio),
        fechaFin: self.aIso(datos.fechaFin)
      };
      // Se borra antes de pedir, no al llegar la respuesta: si la petición falla, el semáforo
      // anterior se quedaría en pantalla como si fuera el de estas fechas.
      self.invalidarComprobacion();
      const turno = self.turnoComprobacion;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhCoincidencias.evaluar({
        username: solicitud.username,
        fechaInicio: solicitud.fechaInicio,
        fechaFin: solicitud.fechaFin,
        incluirAlternativas: true
      })
        .then(function(res) {
          if (turno != self.turnoComprobacion) return;
          self.evaluacion = res || null;
          // Se guardan las fechas ya evaluadas, no las del formulario: si alguien las cambia
          // sin volver a comprobar, la explicación seguiría siendo la del semáforo que se ve.
          self.solicitudComprobada = self.evaluacion ? solicitud : null;
        })
        .catch(function(e) {
          if (turno != self.turnoComprobacion) return;
          app.dialog.alert(e && e.message ? e.message : self.$t('reglascoincidencia.errorEvaluando'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    // Al revés que `aIso`: se guarda un Date suelto, igual que guarda `rInput` cuando se elige
    // en el calendario. Envolverlo en un array lo rompía, porque `rInput` vuelve a envolver lo
    // que lee y el datepicker recibía `[[Date]]`. Se construye con los campos sueltos porque
    // `new Date('2026-12-28')` es UTC y en España retrocede un día.
    deIso: function(iso) {
      const partes = (iso || '').split('-');
      if (partes.length !== 3) return null;
      return new Date(parseInt(partes[0], 10), parseInt(partes[1], 10) - 1, parseInt(partes[2], 10));
    },
    // El datepicker devuelve un Date (o un array con uno) y el API espera yyyy-mm-dd. Se
    // construye a mano con los campos locales: toISOString pasa por UTC y en España adelanta
    // o atrasa el día.
    aIso: function(valor) {
      var fecha = Array.isArray(valor) ? valor[0] : valor;
      if (!fecha) return null;
      if (typeof fecha == 'string') return fecha.length > 10 ? fecha.substring(0, 10) : fecha;
      var d = new Date(fecha);
      var mes = ('0' + (d.getMonth() + 1)).slice(-2);
      var dia = ('0' + d.getDate()).slice(-2);
      return d.getFullYear() + '-' + mes + '-' + dia;
    },
    /**
     * [revisión #1711] Devuelve su promesa, y por eso nunca rechaza: quien escribe algo
     * —guardar, cambiar de estado, pedir propuestas— tiene que poder esperarla antes de soltar
     * el preloader, o la tabla vuelve a ser operable con la lista anterior. Es el mismo criterio
     * que en el mapa de aprobación.
     *
     * <p>[revisión #1711] El turno decide qué respuesta pinta la tabla; el bloqueo lo lleva
     * aparte su espera de la sesión (#663). Son dos cosas distintas: una carga a
     * destiempo no debe pintar nada, pero sí tiene que devolver el bloqueo que pidió, y con un
     * {@code hide} suelto acababa soltando el de la carga buena.</p>
     */
    cargar: function() {
      var self = this;
      var app = self.$f7;
      const turno = ++self.turnoCarga;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      return RhCoincidencias.listar(null)
        .then(function(res) {
          if (turno != self.turnoCarga) return;
          self.reglas = res || [];
        })
        .catch(function(e) {
          if (turno != self.turnoCarga) return;
          if (self.es403(e)) self.error403 = true;
          else app.dialog.alert(e && e.message ? e.message : self.$t('reglascoincidencia.errorCargando'));
        })
        .then(function() {
          espera.cerrar();
        });
    }
  }
};
</script>
